import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useFormikContext } from 'formik';
import axios from 'axios';
import get from 'lodash/get';

import * as ProcessModule from 'modules/process';

import * as Actions from '../actions';
import * as Constants from '../constants';
import { useTranslation } from 'react-i18next';
import Button from 'components/Button';

interface IProps {
  applicationId: number;
  field: ProcessModule.Types.IEntity.Field;
}

let cancelSource = axios.CancelToken.source();

const ActionButton: React.FC<IProps> = ({ applicationId, field }) => {
  const dispatch = useDispatch();
  const {
    i18n: { language }
  } = useTranslation();
  const [isLoading, setLoading] = useState(false);
  const { values, setValues } = useFormikContext();
  const { process, info } = ProcessModule.Hooks.useProcess();

  useEffect(() => () => cancelSource.cancel('canceled'), []);

  function onClick() {
    if (!field.action.key) return;
    const fields = field.action.ids.map((id) => {
      const fieldsByActionId = info.fields.byActionId[process.currentStep]
      const fieldKey = fieldsByActionId[id];
      return {
        key: id,
        value: get(values, `${fieldKey}.value`, ""),
      }
    })

    setLoading(true);
    cancelSource = axios.CancelToken.source();
    dispatch(
      Actions.ActionEvent.request({
        values: {
          applicationId,
          key: field.action.key,
          language,
          fields
        },
        cancelSource,
        callback: {
          onSuccess: ({ fields, values }) => setFieldsValues(fields, values),
          onError: ({ fields, values }) => setFieldsValues(fields, values),
          onFinally: () => setLoading(false)
        }
      })
    );
  }

  const setFieldsValues = (fields, fieldsValues) => {
    const newValuesOptions = Object.keys(fields || {}).reduce((prev, key) => {
      const type = get(fields, `${key}.type`);
      const options = get(fields, `${key}.options`) || [];
      const field = get(values, `${key}`);
      const readonly = get(field, 'readonly');
      if (type === Constants.FIELD_TYPE.DROP_DOWN) {
        const option = options.find(option => String(get(option, 'code')) === String(get(field, 'value')));
        if (!option) {
          return { ...prev, [key]: { key, value: '', option: undefined, readonly } };
        }
        return { ...prev, [key]: { key, value: get(field, 'value'), option: get(option, 'name'), readonly } };
      }
      return { ...prev };
    }, {});

    const newValues = Object.entries(fieldsValues).reduce((prev, [key, fieldValue]) => {
      const value = get(fieldValue, 'value');
      const readonly = get(fieldValue, 'readonly');
      if (value === undefined) {
        return { ...prev };
      }
      return { ...prev, [key]: { key: key, value, readonly } };
    }, {});

    setValues(values => ({ ...((values || {}) as object), ...newValuesOptions, ...newValues }));
  };

  return (
    <div style={{
      width: "100%",
      margin: '30px 0 20px 0'
    }}>
      <Button htmlType={"button"} block={field.block} onClick={onClick} isLoading={isLoading} disabled={field.disabled}>
        {field.title[language]}
      </Button>
    </div>
  );
};

export default ActionButton;
